// Drafts tab — derives the Case's drafts from the audit_log
// (case.draft.create rows) and links each to its DWC-154 viewer.
//
// Why audit_log instead of a dedicated GET /cases/{id}/drafts: Phase 5
// didn't add that endpoint (drafts are fetched individually by id).
// The audit row carries the draft_id in grant_reason ("draft_id=N,..."),
// which is enough for the UI to show a sortable list without a new
// backend route.

import { Link } from "react-router-dom";

import { cases as casesApi } from "../../lib/api";
import { useAsync } from "../../hooks/useAsync";

interface DraftRef {
  draftId: number;
  ts: string;
  kind: string;
  targetType: string;
}

function parseDraftFromGrantReason(grant: string | null): DraftRef | null {
  if (!grant) return null;
  const m = /draft_id=(\d+)/.exec(grant);
  if (!m || !m[1]) return null;
  const kindMatch = /kind=([\w-]+)/.exec(grant);
  const targetMatch = /target_type=(\w+)/.exec(grant);
  return {
    draftId: Number(m[1]),
    ts: "",
    kind: kindMatch?.[1] ?? "dwc-154",
    targetType: targetMatch?.[1] ?? "—",
  };
}

interface Props {
  caseId: number;
}

export function CaseDraftsTab({ caseId }: Props): JSX.Element {
  const { data, loading, error } = useAsync(() => casesApi.activity(caseId), [caseId]);
  const drafts: DraftRef[] = [];
  if (data) {
    for (const e of data.entries) {
      if (e.action !== "case.draft.create") continue;
      const ref = parseDraftFromGrantReason(e.grant_reason);
      if (ref) drafts.push({ ...ref, ts: e.timestamp });
    }
  }

  return (
    <section className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-medium text-slate-800">Drafts</h2>
        <Link to={`/cases/${caseId}/drafts/new`} className="btn-primary">
          New DWC-154 draft
        </Link>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Generates a Texas DWC-154 complaint draft against a named participant. The Citation
        Verifier rejects unverifiable statutory references; the Mode A heuristics and the
        Phase 6 Cross-Check engine surface defects in the opposing-party documents.
      </p>
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && <p className="text-sm text-red-700">Could not load drafts: {error.message}</p>}
      {!loading && drafts.length === 0 && (
        <p className="text-sm text-slate-600">No drafts generated for this Case yet.</p>
      )}
      {drafts.length > 0 && (
        <ul role="list" className="divide-y divide-slate-200">
          {drafts.map((d) => (
            <li key={d.draftId} className="flex items-center justify-between py-3">
              <div>
                <Link
                  to={`/drafts/${d.draftId}`}
                  className="font-medium text-slate-900 hover:text-brand-700"
                >
                  {d.kind.toUpperCase()} draft #{d.draftId}
                </Link>
                <div className="text-xs text-slate-500">
                  target: {d.targetType.replace(/_/g, " ")} · {new Date(d.ts).toLocaleString()}
                </div>
              </div>
              <Link to={`/drafts/${d.draftId}`} className="btn-secondary">
                Open
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
